import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import Lenis from 'lenis'

import { MASK_TRANSITION_ENTER_DURATION } from '@/contexts/transition'
import { useLayoutStore } from '@/store/layout'
import { useRouterStore } from '@/store/router'

import Scrollbar from './components/Scrollbar'
import { useLenisStore } from './useLenisStore'

export const LenisContext = createContext<{ lenis?: Lenis } | undefined>(
  undefined
)

const LenisProvider = ({ children }: PropsWithChildren) => {
  const [lenis, setLenis] = useState<Lenis>()
  const setState = useLenisStore((state) => state.setState)

  const update = useCallback(
    ({ scroll, limit, progress, direction }: Lenis) => {
      setState({ scroll, limit, progress, direction })
      ScrollTrigger.update()
    },
    [setState]
  )

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })

    const lenis = new Lenis({
      smoothWheel: true,
      wrapper: document.documentElement,
      content: document.body,
      anchors: {
        offset: -100
      }
    })

    setLenis(lenis)

    return () => {
      lenis.destroy()
      setLenis(undefined)
    }
  }, [])

  useEffect(() => {
    if (!lenis) return

    const tick = (time: number) => {
      lenis.raf(time * 1000)
    }

    gsap.ticker.add(tick)

    lenis.on('scroll', update)

    return () => {
      lenis.off('scroll', update)
      gsap.ticker.remove(tick)
    }
  }, [lenis, update])

  useEffect(() => {
    if (!lenis) return

    const unsubscribe = useLayoutStore.subscribe(
      (state) => state.lockScroll,
      (lockScroll) => {
        lockScroll ? lenis.stop() : lenis.start()
      }
    )

    return () => {
      unsubscribe()
    }
  }, [lenis])

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () => {
        gsap.delayedCall(MASK_TRANSITION_ENTER_DURATION + 1, () =>
          setState({ scroll: 0, direction: 0 })
        )
      }
    )

    return () => {
      unsubscribe()
    }
  }, [setState])

  const contextValue = useMemo(() => ({ lenis }), [lenis])

  return (
    <LenisContext.Provider value={contextValue}>
      {children}
      <Scrollbar />
    </LenisContext.Provider>
  )
}

export default LenisProvider
